/**
 * SummaryPage — Live Interview summary / scorecard. Authenticated,
 * recruiter-facing (normal app route, wrapped in RoleRoute/Layout — unlike
 * PreJoinPage/LiveInterviewRoomPage, which are public/token-based). Reached
 * from LiveInterviewTab's "View Summary" link once a session's status is
 * "completed".
 *
 * LAYOUT: hero (score ring + recommendation + status/duration at a glance)
 * -> skill radar + per-skill bars -> per-question accordion carrying the
 * ACTUAL answer content -> session/participants/recording detail. This
 * mirrors the shape Proctored Interview's own ScorecardPage already
 * established in this codebase, and the radar is deliberately the same
 * hand-rolled SVG approach it uses (see SkillRadarChart below) rather than
 * recharts: recharts IS a dependency here, but it is used for the dashboard
 * pie/bar/line charts and has no Radar usage anywhere, so following the
 * existing radar implementation keeps the two scorecards visually identical
 * instead of introducing a second, differently-rendered radar.
 *
 * WHERE THE DATA COMES FROM: every field on this page is an existing column
 * on live_interview_sessions / _questions / _answers, projected by the
 * summary endpoint. `skill_scores` is averaged server-side from the
 * per-question scores (see session_service._average_scores_by_skill) — there
 * is no persisted per-skill or per-criterion breakdown for live interviews,
 * and none was added.
 *
 * A spoken answer is now a first-class one: the candidate's browser records
 * it, the server transcribes it with Whisper, and this page shows BOTH — an
 * audio player above the transcript it was produced from, so a reviewer can
 * check the machine against what was actually said.
 *
 * The one honest caveat left: the recruiter's own "OK" button still records
 * modality="audio" with no recording and no transcript. That is a manual
 * force-advance, not an answer, and it is scored against a placeholder (i.e.
 * near zero) rather than being score-neutral.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon, ArrowDownTrayIcon, InformationCircleIcon, VideoCameraIcon, UsersIcon,
  ClockIcon, VideoCameraSlashIcon, ChartBarIcon, ClipboardDocumentListIcon,
  ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, CodeBracketIcon, PencilSquareIcon,
  MicrophoneIcon, DocumentTextIcon, ChatBubbleLeftRightIcon, LanguageIcon,
  SparklesIcon, PlusIcon, TrashIcon, CommandLineIcon, MapPinIcon,
  CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon,
  ShieldCheckIcon, DevicePhoneMobileIcon, EyeSlashIcon, NoSymbolIcon, SpeakerWaveIcon,
  ComputerDesktopIcon, ArrowsRightLeftIcon, ClipboardDocumentIcon,
} from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'

import LoadingSpinner from '@/components/common/LoadingSpinner'
// Integrity Events labels - shared with the recruiter room's live alerts.
import { integrityEventLabel, isIntegrityFlag } from '@/utils/liveInterviewIntegrity'
import {
  getSessionSummary, getChatMessages, downloadLiveInterviewSummaryPdf,
  createAnnotation, updateAnnotation, deleteAnnotation, generateAiAnnotations, reviewSession,
  getConfigForJob,
  type LiveInterviewSummaryParticipant,
  type LiveInterviewSummaryIntegrityEvent,
  type LiveInterviewSummaryQuestion,
  type LiveInterviewSkillScore,
  type LiveInterviewTranscriptSegment,
  type LiveInterviewAnnotation,
  type AnnotationCategory,
} from '@/services/liveInterviewApi'
import { ReviewReasonModal } from '@/pages/proctoredInterview/ScorecardPage'

const FINAL_RECOMMENDATION_LABELS: Record<string, string> = {
  recommend_hire: 'Recommend Hire',
  borderline: 'Borderline',
  do_not_recommend: 'Do Not Recommend',
}

const FINAL_RECOMMENDATION_BADGE_CLASSES: Record<string, string> = {
  recommend_hire: 'bg-green-50 text-green-700 ring-green-200',
  borderline: 'bg-amber-50 text-amber-700 ring-amber-200',
  do_not_recommend: 'bg-red-50 text-red-700 ring-red-200',
}

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  aborted: 'Aborted',
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  scheduled: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-50 text-blue-700',
  completed: 'bg-green-50 text-green-700',
  aborted: 'bg-red-50 text-red-700',
}

const CONNECTION_STATE_LABELS: Record<string, string> = {
  connected: 'Connected',
  disconnected: 'Disconnected',
  not_connected: 'Not Connected',
}

const CONNECTION_STATE_BADGE_CLASSES: Record<string, string> = {
  connected: 'bg-green-50 text-green-700',
  disconnected: 'bg-amber-50 text-amber-700',
  not_connected: 'bg-gray-100 text-gray-700',
}


// Integrity Events icons - camera/mic ones match Proctored's ScorecardPage
// (INTEGRITY_EVENT_ICONS there); labels come from utils/liveInterviewIntegrity.
const LIVE_INTEGRITY_EVENT_ICONS: Record<string, React.FC<{ className?: string }>> = {
  multiple_persons: UsersIcon,
  no_person: UsersIcon,
  electronic_device: DevicePhoneMobileIcon,
  looking_away: EyeSlashIcon,
  camera_blocked: NoSymbolIcon,
  multiple_audio_sources: SpeakerWaveIcon,
  tab_hidden: ArrowsRightLeftIcon,
  copy_attempt: ClipboardDocumentIcon,
  cut_attempt: ClipboardDocumentIcon,
  paste_attempt: ClipboardDocumentIcon,
  multi_screen_detected: ComputerDesktopIcon,
  permission_denied: VideoCameraSlashIcon,
  geolocation_denied: MapPinIcon,
}

// Same thresholds/colors Proctored Interview's ScorecardPage uses, so a
// score reads identically across the two products.
function scoreColor(score?: number | null): string {
  if (score == null) return 'text-gray-400'
  if (score >= 70) return 'text-green-600'
  if (score >= 40) return 'text-amber-600'
  return 'text-red-600'
}

function scoreBarColor(score?: number | null): string {
  if (score == null) return 'bg-gray-200'
  if (score >= 70) return 'bg-green-500'
  if (score >= 40) return 'bg-amber-500'
  return 'bg-red-500'
}

function scoreHex(score?: number | null): string {
  if (score == null) return '#d1d5db'
  if (score >= 70) return '#22c55e'
  if (score >= 40) return '#f59e0b'
  return '#ef4444'
}

const MODALITY_LABELS: Record<string, string> = {
  audio: 'Verbal',
  text: 'Written',
  code: 'Code',
  whiteboard: 'Whiteboard',
}

function ModalityIcon({ modality }: { modality?: string | null }) {
  const className = 'w-3.5 h-3.5'
  if (modality === 'code') return <CodeBracketIcon className={className} />
  if (modality === 'whiteboard') return <PencilSquareIcon className={className} />
  if (modality === 'audio') return <MicrophoneIcon className={className} />
  return <DocumentTextIcon className={className} />
}

function roleLabel(role: string): string {
  if (role === 'recruiter') return 'Recruiter'
  if (role === 'candidate') return 'Candidate'
  return role || 'Unknown'
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return value
  }
}

// The scoring prompt requires every coding question's rationale to end with
// a sentence starting with the exact literal "Process: " prefix (see
// question_generation.score_answer) — split it out so this page can show it
// as its own clearly-labeled "Debugging Process" block instead of it being
// buried inside the general rationale. Mirrors ScorecardPage.tsx's identical
// helper (Proctored Interview's own summary page).
function splitProcessNote(rationale: string): { main: string; process: string | null } {
  const idx = rationale.lastIndexOf('Process: ')
  if (idx === -1) return { main: rationale, process: null }
  return { main: rationale.slice(0, idx).trim(), process: rationale.slice(idx + 'Process: '.length).trim() }
}

// A verbal question skipped via the recruiter's manual "OK" fallback still counts as answered
// when it was scored (mirrors VMP's answeredCount — see its own hasAnswerContent).
function hasAnswerContent(q: LiveInterviewSummaryQuestion): boolean {
  return Boolean(q.transcript || q.text_content || q.code_content || q.whiteboard_image_data || q.answer_audio_url)
}

function formatDuration(totalSeconds?: number | null): string {
  if (totalSeconds === null || totalSeconds === undefined || Number.isNaN(totalSeconds)) return '—'
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Math.floor(totalSeconds % 60)
  const parts: string[] = []
  if (hours > 0) parts.push(`${hours}h`)
  if (hours > 0 || minutes > 0) parts.push(`${minutes}m`)
  parts.push(`${seconds}s`)
  return parts.join(' ')
}

// mm:ss (call-relative), for the transcript's per-line timestamp chip —
// distinct from formatDateTime's wall-clock rendering above.
function formatTimestamp(totalSeconds: number): string {
  const total = Math.max(0, Math.floor(totalSeconds))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

// How many transcript lines render per "page" — see visibleTranscriptCount's
// own comment at its declaration.
const TRANSCRIPT_PAGE_SIZE = 150

const ANNOTATION_CATEGORY_LABELS: Record<AnnotationCategory, string> = {
  strength: 'Strength',
  concern: 'Concern',
  follow_up: 'Follow-up',
  red_flag: 'Red Flag',
}
const ANNOTATION_CATEGORY_BADGE_CLASSES: Record<AnnotationCategory, string> = {
  strength: 'bg-green-50 text-green-700 ring-green-200',
  concern: 'bg-amber-50 text-amber-700 ring-amber-200',
  follow_up: 'bg-blue-50 text-blue-700 ring-blue-200',
  red_flag: 'bg-red-50 text-red-700 ring-red-200',
}

/** One annotation card — a manual (recruiter-authored) or AI-generated
 * feedback note. Manual ones the CURRENT viewer authored get inline
 * edit/delete; AI ones never do (editing an AI annotation's text is
 * supported at the API layer, but is deliberately out of scope for this
 * pass's UI — deleting one is still available, same as any annotation). */
function AnnotationCard({
  annotation, onDelete,
}: {
  annotation: LiveInterviewAnnotation
  onDelete: (id: string) => void
}) {
  const isAi = annotation.author_type === 'ai'
  return (
    <div className={`rounded-lg border px-3 py-2 text-xs ${isAi ? 'bg-violet-50/60 border-violet-100' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {isAi ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 px-1.5 py-0.5 text-[10px] font-semibold">
            <SparklesIcon className="w-3 h-3" /> AI
          </span>
        ) : (
          <span className="text-[10px] font-semibold text-gray-500">{annotation.author_name || 'Recruiter'}</span>
        )}
        {annotation.category && (
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ${ANNOTATION_CATEGORY_BADGE_CLASSES[annotation.category]}`}>
            {ANNOTATION_CATEGORY_LABELS[annotation.category]}
          </span>
        )}
        <button
          type="button"
          onClick={() => onDelete(annotation.id)}
          className="ml-auto text-gray-300 hover:text-red-500 transition-colors"
          aria-label="Delete annotation"
        >
          <TrashIcon className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-gray-700 whitespace-pre-wrap">{annotation.text}</p>
    </div>
  )
}

/** Small inline "+ Add note" affordance — a category dropdown + textarea,
 * opened on demand rather than always rendered so the transcript doesn't
 * read as a form by default. */
function AddAnnotationForm({
  onSave, onCancel,
}: {
  onSave: (category: AnnotationCategory | null, text: string) => void
  onCancel: () => void
}) {
  const [category, setCategory] = useState<AnnotationCategory | ''>('')
  const [text, setText] = useState('')
  return (
    <div className="rounded-lg border border-dashed border-gray-300 p-2.5 space-y-2">
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value as AnnotationCategory | '')}
        className="text-xs border border-gray-300 rounded-md px-2 py-1 text-gray-700"
      >
        <option value="">No category</option>
        {(Object.keys(ANNOTATION_CATEGORY_LABELS) as AnnotationCategory[]).map((c) => (
          <option key={c} value={c}>{ANNOTATION_CATEGORY_LABELS[c]}</option>
        ))}
      </select>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Add a note…"
        rows={2}
        className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-100 focus:border-purple-400 resize-y"
      />
      <div className="flex items-center gap-2 justify-end">
        <button type="button" onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">
          Cancel
        </button>
        <button
          type="button"
          disabled={!text.trim()}
          onClick={() => onSave(category || null, text)}
          className="text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-md px-2.5 py-1"
        >
          Save
        </button>
      </div>
    </div>
  )
}

/** One transcript line + its annotation thread — the accordion-style
 * expand-in-place UI the plan calls for, rather than a separate modal. */
function TranscriptLine({
  segment, annotations, onSeek, onDelete, onAddAnnotation,
}: {
  segment: LiveInterviewTranscriptSegment
  annotations: LiveInterviewAnnotation[]
  onSeek: (seconds: number) => void
  onDelete: (id: string) => void
  onAddAnnotation: (category: AnnotationCategory | null, text: string) => void
}) {
  const [threadOpen, setThreadOpen] = useState(annotations.length > 0)
  const [addingNote, setAddingNote] = useState(false)
  const isRecruiter = segment.role === 'recruiter'
  return (
    <div className={`group flex flex-col ${isRecruiter ? 'items-end' : 'items-start'}`}>
      <div className="flex items-center gap-1.5 max-w-[85%]">
        {!isRecruiter && (
          <button
            type="button"
            onClick={() => setAddingNote((v) => !v)}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 hover:text-purple-600 flex-shrink-0"
            aria-label="Add note on this line"
          >
            <PlusIcon className="w-3.5 h-3.5" />
          </button>
        )}
        <div
          className={`rounded-lg px-3 py-1.5 text-sm border-l-2 ${
            annotations.length > 0 ? 'border-l-purple-400' : 'border-l-transparent'
          } ${isRecruiter ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-800'}`}
        >
          {segment.text}
        </div>
        {isRecruiter && (
          <button
            type="button"
            onClick={() => setAddingNote((v) => !v)}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 hover:text-purple-600 flex-shrink-0"
            aria-label="Add note on this line"
          >
            <PlusIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => onSeek(segment.start_seconds)}
        className="text-[11px] text-gray-400 hover:text-purple-600 mt-0.5 flex items-center gap-1"
        title="Jump to this moment in the recording"
      >
        {segment.speaker_name || roleLabel(segment.role)} · {formatTimestamp(segment.start_seconds)}
        {annotations.length > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setThreadOpen((v) => !v) }}
            className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-purple-50 text-purple-600 px-1.5 py-0.5 text-[10px] font-semibold"
          >
            {annotations.length} note{annotations.length === 1 ? '' : 's'}
          </button>
        )}
      </button>
      {threadOpen && annotations.length > 0 && (
        <div className={`w-full max-w-[85%] mt-1.5 space-y-1.5 ${isRecruiter ? 'items-end' : ''}`}>
          {annotations.map((a) => <AnnotationCard key={a.id} annotation={a} onDelete={onDelete} />)}
        </div>
      )}
      {addingNote && (
        <div className="w-full max-w-[85%] mt-1.5">
          <AddAnnotationForm
            onSave={(category, text) => { onAddAnnotation(category, text); setAddingNote(false) }}
            onCancel={() => setAddingNote(false)}
          />
        </div>
      )}
    </div>
  )
}

/** Session-level annotations (transcript_segment_id null) — not pinned to
 * any one line, shown as their own small list above the line-by-line
 * transcript. */
function GeneralAnnotations({
  annotations, onDelete, onAdd,
}: {
  annotations: LiveInterviewAnnotation[]
  onDelete: (id: string) => void
  onAdd: (category: AnnotationCategory | null, text: string) => void
}) {
  const [adding, setAdding] = useState(false)
  if (annotations.length === 0 && !adding) {
    return (
      <button
        type="button"
        onClick={() => setAdding(true)}
        className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-purple-600"
      >
        <PlusIcon className="w-3.5 h-3.5" /> Add a general note (not tied to a specific line)
      </button>
    )
  }
  return (
    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">General Notes</p>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="text-gray-400 hover:text-purple-600">
            <PlusIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {annotations.map((a) => <AnnotationCard key={a.id} annotation={a} onDelete={onDelete} />)}
      {adding && (
        <AddAnnotationForm
          onSave={(category, text) => { onAdd(category, text); setAdding(false) }}
          onCancel={() => setAdding(false)}
        />
      )}
    </div>
  )
}

function durationLabel(startedAt?: string | null, endedAt?: string | null): string {
  if (!startedAt) return '—'
  if (!endedAt) return 'In progress'
  const totalSeconds = Math.max(0, Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000))
  return formatDuration(totalSeconds)
}

/**
 * Compact SVG radar giving the at-a-glance shape of skill performance.
 * Pure math + inline SVG, no charting library — the same implementation
 * Proctored Interview's ScorecardPage uses, kept identical on purpose so
 * both scorecards render a skill profile the same way.
 *
 * Returns null below 3 skills: a "radar" of one or two axes is a line, not a
 * shape, and reads as a broken chart rather than a sparse one. The caller
 * falls back to the bar list alone in that case.
 */
// Radar axis labels: wrapped onto up to 3 lines (never cut mid-name at a fixed
// width), anchored away from the chart so side labels grow outward, and
// stacked upward/downward for the top/bottom axes so nothing overlaps the
// chart. The full name is always available as a hover tooltip.
const RADAR_LABEL_CHARS = 14
const RADAR_LABEL_LINES = 3
const RADAR_LABEL_LINE_HEIGHT = 11

function wrapRadarLabel(name: string): string[] {
  const words = name.trim().split(/\s+/).flatMap((w) => {
    const parts: string[] = []
    for (let i = 0; i < w.length; i += RADAR_LABEL_CHARS) parts.push(w.slice(i, i + RADAR_LABEL_CHARS))
    return parts
  })
  const lines: string[] = []
  for (const word of words) {
    const last = lines[lines.length - 1]
    if (last !== undefined && `${last} ${word}`.length <= RADAR_LABEL_CHARS) lines[lines.length - 1] = `${last} ${word}`
    else lines.push(word)
  }
  if (lines.length > RADAR_LABEL_LINES) {
    const kept = lines.slice(0, RADAR_LABEL_LINES)
    kept[RADAR_LABEL_LINES - 1] = `${kept[RADAR_LABEL_LINES - 1].slice(0, RADAR_LABEL_CHARS - 1)}…`
    return kept
  }
  return lines
}

function RadarLabel({ name, x, y, center }: { name: string; x: number; y: number; center: number }) {
  const lines = wrapRadarLabel(name)
  const anchor = Math.abs(x - center) < 4 ? 'middle' : x > center ? 'start' : 'end'
  const span = (lines.length - 1) * RADAR_LABEL_LINE_HEIGHT
  // Top axis grows upward, bottom axis grows downward, sides are centred on the point.
  const firstY = y < center - 4 ? y - span : y > center + 4 ? y : y - span / 2
  return (
    <text x={x} textAnchor={anchor} dominantBaseline="middle" fontSize={10} fill="#4b5563">
      <title>{name}</title>
      {lines.map((line, i) => (
        <tspan key={i} x={x} y={firstY + i * RADAR_LABEL_LINE_HEIGHT}>{line}</tspan>
      ))}
    </text>
  )
}

function SkillRadarChart({ skills }: { skills: LiveInterviewSkillScore[] }) {
  const n = skills.length
  if (n < 3) return null
  const size = 340
  const center = size / 2
  const maxR = 80
  const angleStep = (2 * Math.PI) / n
  const pointFor = (i: number, r: number): [number, number] => {
    const angle = angleStep * i - Math.PI / 2
    return [center + r * Math.cos(angle), center + r * Math.sin(angle)]
  }
  const pathFrom = (points: [number, number][]) =>
    `${points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')}Z`

  const dataPoints = skills.map((skill, i) =>
    pointFor(i, maxR * (Math.max(0, Math.min(100, skill.score ?? 0)) / 100))
  )

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[340px] mx-auto overflow-visible">
      {[0.25, 0.5, 0.75, 1].map((r) => (
        <path
          key={r}
          d={pathFrom(skills.map((_, i) => pointFor(i, maxR * r)))}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={1}
        />
      ))}
      {skills.map((_, i) => {
        const [x, y] = pointFor(i, maxR)
        return <line key={i} x1={center} y1={center} x2={x} y2={y} stroke="#e5e7eb" strokeWidth={1} />
      })}
      <path d={pathFrom(dataPoints)} fill="rgba(124,58,237,0.16)" stroke="#7c3aed" strokeWidth={1.5} />
      {dataPoints.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={2.5} fill="#7c3aed" />
      ))}
      {skills.map((skill, i) => {
        const [x, y] = pointFor(i, maxR + 12)
        return <RadarLabel key={skill.skill_name} name={skill.skill_name} x={x} y={y} center={center} />
      })}
    </svg>
  )
}

/** Collapsible card shell — same pattern as Proctored Interview's scorecard. */
function Section({
  title, icon, summary, peek, defaultOpen = true, children,
}: {
  title: string
  icon?: React.ReactNode
  summary?: React.ReactNode
  // Shown under the header whether the section is open or not (e.g. the
  // Integrity Events badges) - same as Proctored ScorecardPage's Section.
  peek?: React.ReactNode
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
      >
        {icon}
        <h3 className="text-sm font-semibold text-gray-900 flex-1">{title}</h3>
        {summary}
        {open ? (
          <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {peek && <div className="px-5 pb-4 pt-1">{peek}</div>}
      {open && <div className="px-5 pb-5 pt-1 border-t border-gray-100">{children}</div>}
    </div>
  )
}

/**
 * One question's accordion row: always-visible header (sequence, skill,
 * status, score), and on expand the full question text plus whatever the
 * candidate actually produced for it — transcript, written answer, code
 * (with its test-case results), or whiteboard sketch — followed by the AI's
 * scoring rationale.
 */
function QuestionCard({
  question, expanded, onToggle, note,
}: {
  question: LiveInterviewSummaryQuestion
  expanded: boolean
  onToggle: () => void
  note?: string
}) {
  const testResults = question.test_case_results || []
  const passedCount = testResults.filter((r) => r.passed).length
  const runHistory = question.run_history || []
  const isCoding = question.modality === 'code'
  const [showRunHistory, setShowRunHistory] = React.useState(false)
  const [expandedRuns, setExpandedRuns] = React.useState<Set<number>>(new Set())
  const toggleRun = (idx: number) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }
  // A playable recording counts as answer content in its own right: a clip
  // whose transcription came back empty (a near-silent answer) still has
  // something for a reviewer to listen to, and showing "no answer content was
  // recorded" directly above an audio player would be plainly wrong.
  const hasAnswerBody = Boolean(
    question.transcript || question.text_content || question.code_content
    || question.whiteboard_image_data || question.answer_audio_url
  )

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-3 px-3.5 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        {expanded ? (
          <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
        ) : (
          <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-purple-700">
              #{question.sequence_number} · {question.skill_name}
            </span>
            {question.modality && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                <ModalityIcon modality={question.modality} />
                {MODALITY_LABELS[question.modality] || question.modality}
              </span>
            )}
            {question.status === 'answered' ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700">Answered</span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {question.status}
              </span>
            )}
          </div>
          <p className={`text-sm text-gray-800 mt-1 ${expanded ? '' : 'line-clamp-2'}`}>{question.question_text}</p>
        </div>
        <span className={`text-base font-bold flex-shrink-0 ${scoreColor(question.rubric_score)}`}>
          {question.rubric_score != null ? Math.round(question.rubric_score) : '—'}
        </span>
      </button>

      {expanded && (
        <div className="px-3.5 pb-3.5 pt-1 border-t border-gray-100 space-y-3">
          {question.duration_minutes != null && (
            <p className="text-[11px] text-gray-400">Allotted: {question.duration_minutes} min</p>
          )}

          {/* The recording of a spoken answer, above the transcript it was
              transcribed from — so a reviewer can check the machine's
              transcription against what was actually said, and hear the tone,
              hesitation and back-and-forth that no transcript carries.

              Rendered only when the server actually found a clip (it
              existence-checks blob storage per load; see the summary
              endpoint). A verbal question the recruiter handed a text box for
              has `text_content` and no recording, and falls through to the
              typed answer below — which is why this branch never has to
              special-case that: no clip, no player. */}
          {question.answer_audio_url && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                Recorded Answer
              </p>
              <audio controls preload="none" src={question.answer_audio_url} className="w-full">
                Your browser cannot play this recording.
              </audio>
              <p className="text-[11px] text-gray-400 mt-1">
                Captures both sides of the conversation — the interviewer&apos;s question as well as
                the candidate&apos;s answer.
              </p>
            </div>
          )}

          {question.transcript && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Transcript</p>
              <blockquote className="text-sm text-gray-700 bg-gray-50 border-l-2 border-gray-300 rounded-r-md px-3 py-2 italic whitespace-pre-wrap">
                {question.transcript}
              </blockquote>
            </div>
          )}

          {question.text_content && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">
                {question.whiteboard_image_data ? 'Notes' : "Candidate's Answer"}
              </p>
              <blockquote className="text-sm text-gray-700 bg-gray-50 border-l-2 border-gray-300 rounded-r-md px-3 py-2 italic whitespace-pre-wrap">
                {question.text_content}
              </blockquote>
            </div>
          )}

          {question.code_content && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                Code{question.code_language ? ` · ${question.code_language}` : ''}
              </p>
              <pre className="text-xs text-gray-100 bg-[#1e1e1e] rounded-lg p-3 overflow-x-auto whitespace-pre">
                {question.code_content}
              </pre>
            </div>
          )}

          {testResults.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                Test Cases · {passedCount}/{testResults.length} passed
              </p>
              <div className="space-y-1.5">
                {testResults.map((result, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs">
                    <span
                      className={`px-1.5 py-0.5 rounded font-mono font-medium flex-shrink-0 ${
                        result.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {result.passed ? 'PASS' : 'FAIL'}
                    </span>
                    <span className="text-gray-600">
                      {result.description || `Test ${idx + 1}`}
                      {result.explanation ? ` — ${result.explanation}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isCoding && runHistory.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowRunHistory((v) => !v)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-left"
              >
                <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <CommandLineIcon className="w-3.5 h-3.5 text-gray-400" />
                  Code Run History ({runHistory.length} attempt{runHistory.length === 1 ? '' : 's'})
                </span>
                {showRunHistory ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />}
              </button>
              {showRunHistory && (
                <div className="divide-y divide-gray-100">
                  {runHistory.map((run, idx) => {
                    const isRunOpen = expandedRuns.has(idx)
                    const runPassCount = run.results?.filter((r) => r.passed).length
                    const runTotalCount = run.results?.length
                    return (
                      <div key={idx}>
                        <button
                          type="button"
                          onClick={() => toggleRun(idx)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-1.5 hover:bg-gray-50 text-left"
                        >
                          <span className="flex items-center gap-2 text-xs">
                            <span className="text-gray-400 font-mono w-5 flex-shrink-0">#{idx + 1}</span>
                            <span className={`px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${run.action === 'run_tests' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                              {run.action === 'run_tests' ? 'Run Test Cases' : 'Run'}
                            </span>
                            <span className="text-gray-400">{formatDateTime(run.at)}</span>
                            {runTotalCount !== undefined && (
                              <span className={runPassCount === runTotalCount ? 'text-green-600' : 'text-amber-600'}>
                                {runPassCount}/{runTotalCount} passed
                              </span>
                            )}
                            {run.exit_code != null && run.action === 'run' && (
                              <span className={run.exit_code === 0 ? 'text-green-600' : 'text-red-600'}>
                                exit {run.exit_code}
                              </span>
                            )}
                          </span>
                          {isRunOpen ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                        </button>
                        {isRunOpen && (
                          <div className="px-3 pb-2.5">
                            <pre className="text-[11px] text-gray-100 bg-[#1e1e1e] rounded-md px-3 py-2 overflow-x-auto font-mono leading-relaxed mb-1.5">
                              {run.code}
                            </pre>
                            {run.action === 'run_tests' && run.results && run.results.length > 0 && (
                              <div className="space-y-1">
                                {run.results.map((r, ri) => (
                                  <div key={ri} className="flex items-start gap-1.5 text-[11px]">
                                    <span className={`px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${r.passed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                      {r.passed ? 'PASS' : 'FAIL'}
                                    </span>
                                    <span className="text-gray-600">{r.description}{r.explanation ? ` — ${r.explanation}` : ''}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {run.action === 'run' && (run.stdout || run.stderr) && (
                              <div className="text-[11px] font-mono space-y-1">
                                {run.stdout && <pre className="text-gray-600 whitespace-pre-wrap">{run.stdout}</pre>}
                                {run.stderr && <pre className="text-red-600 whitespace-pre-wrap">{run.stderr}</pre>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {question.whiteboard_image_data && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Whiteboard</p>
              <img
                src={question.whiteboard_image_data}
                alt={`Whiteboard sketch for question ${question.sequence_number}`}
                className="w-full rounded-lg border border-gray-200 bg-white"
              />
            </div>
          )}

          {!hasAnswerBody && (
            <p className="text-xs text-gray-400">
              {question.modality === 'audio'
                ? 'Marked as asked by the interviewer — no answer was recorded or transcribed.'
                : 'No answer content was recorded for this question.'}
            </p>
          )}

          {question.score_rationale && (() => {
            const { main, process } = isCoding
              ? splitProcessNote(question.score_rationale)
              : { main: question.score_rationale, process: null }
            return (
              <>
                <div className="flex items-start gap-1.5">
                  <InformationCircleIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-600 whitespace-pre-wrap">{main}</p>
                </div>
                {process && (
                  <div className="flex items-start gap-1.5 bg-indigo-50 rounded-lg px-3 py-2">
                    <CommandLineIcon className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-indigo-700">Debugging Process</p>
                      <p className="text-xs text-indigo-900 whitespace-pre-wrap">{process}</p>
                    </div>
                  </div>
                )}
              </>
            )
          })()}

          {question.rubric_criteria_scores && question.rubric_criteria_scores.length > 0 && (
            <div className="pt-3 border-t border-gray-100 space-y-1">
              {question.rubric_criteria_scores.map((c, idx) => (
                <div key={idx} className="text-xs">
                  <div className="flex items-center justify-between text-gray-700">
                    <span className="font-medium">{c.criterion}</span>
                    <span className={scoreColor(c.score)}>{c.score?.toFixed(0)}</span>
                  </div>
                  {c.feedback && <p className="text-gray-500">{c.feedback}</p>}
                </div>
              ))}
            </div>
          )}

          {note && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Recruiter Note</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{note}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function SummaryPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set())
  const [downloading, setDownloading] = useState(false)

  // Which decision the recruiter is currently confirming a reason for, or
  // null when the modal is closed — same pattern Proctored Interview's own
  // ScorecardPage uses (see its ReviewReasonModal, reused here as-is).
  const [reviewAction, setReviewAction] = useState<'approved' | 'rejected' | null>(null)
  const [reviewReason, setReviewReason] = useState('')

  const { data: summary, isLoading, isError } = useQuery({
    queryKey: ['live-interview-summary', sessionId],
    queryFn: () => getSessionSummary(sessionId as string),
    enabled: !!sessionId,
  })

  // This job's rubric criteria descriptions ("what a strong answer for this
  // skill covers"), read-only here — same config the Live Interview tab's
  // skills/rubrics editor manages. Feeds the Skill Breakdown descriptions
  // below the radar, mirroring VMP's summary page (which fetches its own
  // skillsRubrics for the identical purpose).
  const { data: config } = useQuery({
    queryKey: ['live-interview-config', summary?.job_id],
    queryFn: () => getConfigForJob(summary!.job_id),
    enabled: !!summary?.job_id,
  })
  const rubricCriteriaBySkill = useMemo(() => {
    const map = new Map<string, { name: string; description?: string | null }[]>()
    for (const skill of config?.skills_rubrics || []) {
      map.set(skill.skill_name, skill.rubric_criteria || [])
    }
    return map
  }, [config])

  const reviewMutation = useMutation({
    mutationFn: ({ status, reason }: { status: 'approved' | 'rejected'; reason: string }) =>
      reviewSession(sessionId!, status, reason),
    onSuccess: (updated) => {
      queryClient.setQueryData(['live-interview-summary', sessionId], updated)
      // The decision also moves the candidate's pipeline stage and Stage
      // Acknowledgement state — mark those (and the job's interview lists)
      // stale so Apply Matching and the interview tabs show it without a reload.
      for (const key of ['stage-acknowledgements', 'comparison-results', 'live-interview-sessions', 'live-interview-candidates', 'proctored-interview-submissions']) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      setReviewAction(null)
      setReviewReason('')
      toast.success(updated.review_status === 'approved' ? 'Candidate approved' : 'Candidate rejected')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to record the decision'),
  })

  const { data: chatMessages } = useQuery({
    queryKey: ['live-interview-chat', sessionId],
    queryFn: () => getChatMessages(sessionId as string),
    enabled: !!sessionId,
  })

  // Timestamp-Linked Feedback Annotations — a local, mutable copy of
  // summary.annotations (create/update/delete/generate all act on this
  // directly rather than refetching the whole summary for every small
  // edit). Re-seeded whenever the underlying query data changes (e.g. the
  // first successful load) — see the effect below.
  const [annotations, setAnnotations] = useState<LiveInterviewAnnotation[]>([])
  const [annotationsSeeded, setAnnotationsSeeded] = useState(false)
  useEffect(() => {
    if (summary && !annotationsSeeded) {
      setAnnotations(summary.annotations || [])
      setAnnotationsSeeded(true)
    }
  }, [summary, annotationsSeeded])
  const [generatingAiAnnotations, setGeneratingAiAnnotations] = useState(false)
  const hasAiAnnotations = annotations.some((a) => a.author_type === 'ai')

  // Lazy-loading the transcript list — an hours-long interview's FULL,
  // persisted transcript (unlike the live in-call view, this is never
  // capped/dropped) can be thousands of lines; rendering all of them the
  // moment this section is expanded would visibly jank a long report. Starts
  // showing only the most recent page-size-worth of lines (server-configured,
  // see transcriptPageSize below; what a recruiter reviewing right after the
  // call most likely wants first) with a "Load earlier lines" affordance to
  // reveal more in the same-size batches, rather than one unbounded render.
  const transcriptPageSize = summary?.transcript_report_page_size || TRANSCRIPT_PAGE_SIZE
  const [visibleTranscriptCount, setVisibleTranscriptCount] = useState(TRANSCRIPT_PAGE_SIZE)
  useEffect(() => {
    if (summary) setVisibleTranscriptCount(transcriptPageSize)
    // Only re-seed off the summary load itself, not every render where
    // transcriptPageSize is recomputed (same value each time regardless).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary])
  const recordingVideoRef = useRef<HTMLVideoElement>(null)
  const seekRecordingTo = (seconds: number) => {
    const video = recordingVideoRef.current
    if (!video) return
    video.currentTime = seconds
    video.scrollIntoView({ behavior: 'smooth', block: 'center' })
    video.play().catch(() => {})
  }

  const handleGenerateAiAnnotations = async () => {
    if (!sessionId || generatingAiAnnotations || hasAiAnnotations) return
    setGeneratingAiAnnotations(true)
    try {
      const generated = await generateAiAnnotations(sessionId)
      setAnnotations((prev) => [...prev, ...generated])
    } catch (err: any) {
      if (err?.response?.status === 409) {
        toast.error('AI annotations have already been generated for this session.')
      } else if (err?.response?.status === 400) {
        toast.error('No transcript is available to analyze yet.')
      } else {
        toast.error('Could not generate AI annotations. Please try again.')
      }
    } finally {
      setGeneratingAiAnnotations(false)
    }
  }

  const handleCreateAnnotation = async (
    transcriptSegmentId: string | null, category: AnnotationCategory | null, text: string
  ) => {
    if (!sessionId || !text.trim()) return
    try {
      const created = await createAnnotation(sessionId, transcriptSegmentId, category, text.trim())
      setAnnotations((prev) => [...prev, created])
    } catch {
      toast.error('Could not save the annotation. Please try again.')
    }
  }

  const handleUpdateAnnotation = async (
    annotationId: string, updates: { text?: string; category?: AnnotationCategory | null }
  ) => {
    try {
      const updated = await updateAnnotation(annotationId, updates)
      setAnnotations((prev) => prev.map((a) => (a.id === annotationId ? updated : a)))
    } catch {
      toast.error('Could not update the annotation. Please try again.')
    }
  }

  const handleDeleteAnnotation = async (annotationId: string) => {
    try {
      await deleteAnnotation(annotationId)
      setAnnotations((prev) => prev.filter((a) => a.id !== annotationId))
    } catch {
      toast.error('Could not delete the annotation. Please try again.')
    }
  }

  const skillScores = useMemo(
    // Highest first, so the radar's labels and the bar list below it read in
    // the same order and the strongest skills lead.
    () => [...(summary?.skill_scores || [])].sort((a, b) => b.score - a.score),
    [summary?.skill_scores]
  )

  const questions = summary?.questions || []
  const allExpanded = questions.length > 0 && expandedQuestions.size === questions.length

  const answeredCount = questions.filter((q) => q.rubric_score != null || hasAnswerContent(q)).length

  // One-line lead sentence for the recommendation, restating the bare label as plain English a
  // hiring manager can act on — same computation VMP's own summary page's hero card shows.
  const recommendationSummarySentence = useMemo(() => {
    if (!summary) return ''
    if (summary.final_recommendation == null) {
      return 'The interview has not been scored yet — a recommendation will appear once it is completed.'
    }
    const label = FINAL_RECOMMENDATION_LABELS[summary.final_recommendation] || summary.final_recommendation
    const scoreText = summary.final_score != null ? `an overall score of ${Math.round(summary.final_score)}/100` : 'no overall score'
    return `${label} — based on ${scoreText} across ${answeredCount} of ${questions.length} questions answered.`
  }, [summary, answeredCount, questions.length])

  // Bullet-point evidence behind the recommendation, synthesized entirely from data already on
  // the summary (skill scores + each question's own score_rationale) — mirrors VMP's
  // recommendationSupportingPoints exactly, so both apps explain the "why" the same way.
  const recommendationSupportingPoints = useMemo(() => {
    if (!summary) return [] as string[]
    const points: string[] = []
    const sortedSkills = [...(summary.skill_scores || [])].sort((a, b) => a.score - b.score)
    if (sortedSkills.length >= 2) {
      const weakest = sortedSkills[0]
      const strongest = sortedSkills[sortedSkills.length - 1]
      if (weakest.skill_name !== strongest.skill_name) {
        points.push(`Strongest area: ${strongest.skill_name} (${Math.round(strongest.score)}/100). Weakest area: ${weakest.skill_name} (${Math.round(weakest.score)}/100).`)
      }
    } else if (sortedSkills.length === 1) {
      points.push(`${sortedSkills[0].skill_name}: ${Math.round(sortedSkills[0].score)}/100.`)
    }

    const totalQuestions = questions.length
    const unanswered = totalQuestions - answeredCount
    if (unanswered > 0) {
      points.push(`${unanswered} of ${totalQuestions} question${totalQuestions === 1 ? '' : 's'} went unanswered.`)
    }

    const scoredQuestions = questions.filter((q) => q.rubric_score != null && q.score_rationale)
    if (scoredQuestions.length > 0) {
      const sorted = [...scoredQuestions].sort((a, b) => (a.rubric_score ?? 0) - (b.rubric_score ?? 0))
      const lowest = sorted[0]
      points.push(`Q${lowest.sequence_number} (${lowest.skill_name}, ${Math.round(lowest.rubric_score ?? 0)}/100): ${lowest.score_rationale}`)
      const highest = sorted[sorted.length - 1]
      if (highest.question_id !== lowest.question_id && (highest.rubric_score ?? 0) >= 70) {
        points.push(`Q${highest.sequence_number} (${highest.skill_name}, ${Math.round(highest.rubric_score ?? 0)}/100): ${highest.score_rationale}`)
      }
    }

    return points
  }, [summary, questions, answeredCount])

  const notes = summary?.notes || []
  const mainNote = notes.find((n) => !n.question_id)
  const questionNotes = notes.filter((n) => n.question_id)

  const toggleQuestion = (questionId: string) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev)
      if (next.has(questionId)) next.delete(questionId)
      else next.add(questionId)
      return next
    })
  }

  const toggleAll = () => {
    setExpandedQuestions(allExpanded ? new Set() : new Set(questions.map((q) => q.question_id)))
  }

  const handleDownloadPdf = async () => {
    if (!sessionId) return
    setDownloading(true)
    try {
      await downloadLiveInterviewSummaryPdf(sessionId, summary?.candidate_name)
    } catch {
      toast.error('Failed to download the PDF. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  const overallPct = Math.max(0, Math.min(100, summary?.final_score ?? 0))
  const hasReviewDecision = !!summary?.review_status

  return (
    <>
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
      {/* Top action bar — Back on the left, decision/export actions on the
          right, in a white bar like Proctored Interview's scorecard. */}
      <div className="sticky top-0 z-10 bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => (summary ? navigate(`/jobs/${summary.job_id}?tab=liveInterview`) : navigate(-1))}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back
        </button>
        {summary?.status === 'completed' && !hasReviewDecision && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setReviewAction('approved')}
              disabled={reviewMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-green-300 text-green-700 rounded-md text-sm font-medium hover:bg-green-50 disabled:opacity-50"
            >
              <CheckCircleIcon className="w-4 h-4" /> Approve
            </button>
            <button
              onClick={() => setReviewAction('rejected')}
              disabled={reviewMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-700 rounded-md text-sm font-medium hover:bg-red-50 disabled:opacity-50"
            >
              <XCircleIcon className="w-4 h-4" /> Reject
            </button>
            <button
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4" /> {downloading ? 'Preparing…' : 'Download PDF'}
            </button>
          </div>
        )}
        {summary?.status === 'completed' && hasReviewDecision && (
          <button
            onClick={handleDownloadPdf}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="w-4 h-4" /> {downloading ? 'Preparing…' : 'Download PDF'}
          </button>
        )}
      </div>

      {isLoading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!isLoading && isError && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-sm text-gray-500">
          The summary is not available for this session.
        </div>
      )}

      {!isLoading && summary && (
        <>
          {/* ---- Hero: the whole outcome in one glance. ------------------ */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-5 flex-wrap">
              {/* Conic-gradient score ring — same treatment as Proctored
                  Interview's scorecard hero. */}
              <div
                className="relative w-[88px] h-[88px] rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: `conic-gradient(${scoreHex(summary.final_score)} ${overallPct}%, #eef0f4 0)` }}
              >
                <div className="absolute inset-[6px] rounded-full bg-white flex flex-col items-center justify-center leading-none">
                  <span className={`text-2xl font-bold ${scoreColor(summary.final_score)}`}>
                    {summary.final_score != null ? Math.round(summary.final_score) : '—'}
                  </span>
                  <span className="text-[10px] text-gray-400 mt-0.5">/100</span>
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-gray-900">Live Interview Summary</h2>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE_CLASSES[summary.status] || STATUS_BADGE_CLASSES.scheduled}`}>
                    {STATUS_LABELS[summary.status] || summary.status}
                  </span>
                  {/* The recruiter's decision, sitting with the summary
                      title rather than among the scores — it is a human
                      judgement about this person, not another number the
                      system produced. Same placement/styling as Proctored
                      Interview's own ScorecardPage. */}
                  {summary.review_status && (
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                        summary.review_status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                      title={
                        summary.reviewed_by_name
                          ? `${summary.review_status === 'approved' ? 'Approved' : 'Rejected'} by ${summary.reviewed_by_name} on ${formatDateTime(summary.reviewed_at)}`
                          : undefined
                      }
                    >
                      {summary.review_status === 'approved' ? 'Approved' : 'Rejected'}
                    </span>
                  )}
                  {summary.final_recommendation && (
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ring-1 ${FINAL_RECOMMENDATION_BADGE_CLASSES[summary.final_recommendation] || 'bg-gray-100 text-gray-700 ring-gray-200'}`}>
                      {FINAL_RECOMMENDATION_LABELS[summary.final_recommendation] || summary.final_recommendation}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                    <ClockIcon className="w-3.5 h-3.5" />
                    {durationLabel(summary.started_at, summary.ended_at)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {questions.length} question{questions.length === 1 ? '' : 's'}
                  </span>
                </div>
                {summary.final_score == null && (
                  <p className="text-xs text-gray-500 mt-2">
                    {summary.status === 'completed'
                      ? 'No questions were answered, so no final score could be computed.'
                      : 'A final score is computed once this interview is ended.'}
                  </p>
                )}
              </div>
            </div>

            {/* Key findings — the evidence behind the recommendation, same card as the hero
                above rather than a separate one repeating the recommendation. */}
            {summary.final_recommendation != null && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-700">{recommendationSummarySentence}</p>
                {recommendationSupportingPoints.length > 0 && (
                  <ul className="list-disc pl-5 mt-1.5 space-y-0.5 text-xs text-gray-600">
                    {recommendationSupportingPoints.map((point, i) => <li key={i}>{point}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>


          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">AI-Generated Feedback Summary</p>
            <p className="text-xs text-gray-400 mt-0.5 mb-3">Based on questions &amp; answers from this session</p>

            {/* Each scored question's own AI rationale, already produced at scoring time — no
                generation step or API call needed, same computed-not-generated "Overall
                Assessment" VMP's own summary page builds from question.scoreRationale. */}
            {questions.length > 0 && questions.some((q) => q.score_rationale) ? (
              <div className="space-y-1.5">
                {questions.filter((q) => q.score_rationale).map((q) => (
                  <p key={q.question_id} className="text-sm text-gray-700">
                    <span className="font-medium text-gray-500">{q.skill_name}:</span>{' '}
                    {q.score_rationale}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">
                No AI feedback is available yet — it appears once at least one question has been scored.
              </p>
            )}
          </div>

          {/* ---- Skill profile: radar + per-skill bars. ------------------ */}
          <Section
            title="Skill Breakdown"
            icon={<ChartBarIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
            summary={
              skillScores.length > 0 ? (
                <span className="text-xs text-gray-400 mr-1">{skillScores.length} skills</span>
              ) : undefined
            }
          >
            {skillScores.length === 0 ? (
              <p className="text-sm text-gray-500 pt-3">
                No skill scores yet — they appear once at least one question has a scored answer.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-3 items-center">
                {/* Below 3 skills the radar renders nothing (a 2-axis radar
                    is a line, not a shape); the bars alone carry it. */}
                <SkillRadarChart skills={skillScores} />
                <div className="space-y-3">
                  {skillScores.map((skill) => (
                    <div key={skill.skill_name}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-700 truncate pr-2">{skill.skill_name}</span>
                        <span className={`font-semibold ${scoreColor(skill.score)}`}>
                          {Math.round(skill.score)}
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${scoreBarColor(skill.score)}`}
                          style={{ width: `${Math.max(0, Math.min(100, skill.score))}%` }}
                        />
                      </div>
                      {(rubricCriteriaBySkill.get(skill.skill_name) || []).length > 0 && (
                        <ul className="mt-1.5 space-y-0.5">
                          {(rubricCriteriaBySkill.get(skill.skill_name) || []).map((c, idx) => (
                            <li key={idx} className="text-[11px] text-gray-500">
                              <span className="font-medium text-gray-600">{c.name}:</span>{' '}
                              {c.description || ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {/* ---- Per-question accordion, with the real answer content. --- */}
          <Section
            title="Questions"
            icon={<ClipboardDocumentListIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
            summary={
              questions.length > 0 ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); toggleAll() }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleAll() }
                  }}
                  className="text-xs text-purple-600 hover:text-purple-800 mr-2 cursor-pointer"
                >
                  {allExpanded ? 'Collapse all' : 'Expand all'}
                </span>
              ) : undefined
            }
          >
            {questions.length === 0 ? (
              <p className="text-sm text-gray-500 pt-3">No questions were generated for this session.</p>
            ) : (
              <div className="space-y-2 pt-3">
                {questions.map((question) => (
                  <QuestionCard
                    key={question.question_id}
                    question={question}
                    expanded={expandedQuestions.has(question.question_id)}
                    onToggle={() => toggleQuestion(question.question_id)}
                    note={questionNotes.find((n) => n.question_id === question.question_id)?.text}
                  />
                ))}
              </div>
            )}
          </Section>

          {/* ---- Recruiter's main note (question_id null) — per-question
              notes render inline under their own question above instead. -- */}
          <Section
            title="Notes"
            icon={<DocumentTextIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
            defaultOpen={false}
            summary={mainNote ? <span className="text-xs text-gray-400 mr-1">1</span> : undefined}
          >
            {mainNote ? (
              <p className="text-sm text-gray-700 whitespace-pre-wrap pt-3">{mainNote.text}</p>
            ) : (
              <p className="text-sm text-gray-500 pt-3">No main note was saved during this session.</p>
            )}
          </Section>

          {/* ---- Chat transcript. ----------------------------------------- */}
          <Section
            title="Chat"
            icon={<ChatBubbleLeftRightIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
            defaultOpen={false}
            summary={
              chatMessages && chatMessages.length > 0 ? (
                <span className="text-xs text-gray-400 mr-1">{chatMessages.length}</span>
              ) : undefined
            }
          >
            {!chatMessages || chatMessages.length === 0 ? (
              <p className="text-sm text-gray-500 pt-3">No chat messages were sent during this session.</p>
            ) : (
              <div className="space-y-2 pt-3">
                {chatMessages.map((m) => (
                  <div key={m.id} className={`flex flex-col ${m.sender_role === 'recruiter' ? 'items-end' : 'items-start'}`}>
                    <div
                      className={`max-w-[75%] rounded-lg px-3 py-1.5 text-sm ${
                        m.sender_role === 'recruiter' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {m.text}
                    </div>
                    <span className="text-[11px] text-gray-400 mt-0.5">
                      {m.sender_name || roleLabel(m.sender_role || '')} · {formatDateTime(m.sent_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* ---- Timestamp-Linked Feedback Annotations: full-session live
              transcript + recruiter/AI annotations. --------------------- */}
          <Section
            title="AI Transcript Summary"
            icon={<LanguageIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
            defaultOpen={false}
            summary={
              summary.transcript_segments.length > 0 ? (
                <span className="text-xs text-gray-400 mr-1">{summary.transcript_segments.length}</span>
              ) : undefined
            }
          >
            {summary.transcript_segments.length === 0 ? (
              <p className="text-sm text-gray-500 pt-3">No live transcript was captured during this session.</p>
            ) : (
              <div className="pt-3 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Captured live during the call — click a timestamp to jump to that moment in the recording.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={generatingAiAnnotations || hasAiAnnotations}
                      onClick={handleGenerateAiAnnotations}
                      title={hasAiAnnotations ? 'AI annotations have already been generated for this session' : undefined}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md px-3 py-1.5"
                    >
                      <SparklesIcon className="w-3.5 h-3.5" />
                      {generatingAiAnnotations ? 'Generating…' : hasAiAnnotations ? 'AI Annotations Generated' : 'Generate AI Annotations'}
                    </button>
                  </div>
                </div>

                {/* AI-generated session-level feedback shown inline in the transcript box */}
                {annotations.filter((a) => a.author_type === 'ai' && !a.transcript_segment_id).length > 0 && (
                  <div className="space-y-2">
                    {annotations
                      .filter((a) => a.author_type === 'ai' && !a.transcript_segment_id)
                      .map((a) => (
                        <div key={a.id} className="rounded-lg border border-violet-100 bg-violet-50/60 px-3 py-2 text-xs">
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 px-1.5 py-0.5 text-[10px] font-semibold">
                              <SparklesIcon className="w-3 h-3" /> AI
                            </span>
                            {a.category && (
                              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ${ANNOTATION_CATEGORY_BADGE_CLASSES[a.category as AnnotationCategory] || ''}`}>
                                {ANNOTATION_CATEGORY_LABELS[a.category as AnnotationCategory] || a.category}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">{a.text}</p>
                        </div>
                      ))}
                  </div>
                )}

                {/* General/session-level manual annotations — not pinned to a line. */}
                <GeneralAnnotations
                  annotations={annotations.filter((a) => a.author_type !== 'ai' && !a.transcript_segment_id)}
                  onDelete={handleDeleteAnnotation}
                  onAdd={(category, text) => handleCreateAnnotation(null, category, text)}
                />

                <div className="space-y-2.5">
                  {summary.transcript_segments.length > visibleTranscriptCount && (
                    <button
                      type="button"
                      onClick={() => setVisibleTranscriptCount((n) => n + transcriptPageSize)}
                      className="w-full text-xs font-medium text-purple-600 hover:text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md py-1.5"
                    >
                      Load earlier lines ({summary.transcript_segments.length - visibleTranscriptCount} more)
                    </button>
                  )}
                  {summary.transcript_segments
                    .slice(-visibleTranscriptCount)
                    .map((segment: LiveInterviewTranscriptSegment) => (
                      <TranscriptLine
                        key={segment.id}
                        segment={segment}
                        annotations={annotations.filter((a) => a.transcript_segment_id === segment.id)}
                        onSeek={seekRecordingTo}
                        onDelete={handleDeleteAnnotation}
                        onAddAnnotation={(category, text) => handleCreateAnnotation(segment.id, category, text)}
                      />
                    ))}
                </div>
              </div>
            )}
          </Section>

          {/* ---- Session details: overview stats + participants, one section. */}
          <Section
            title="Session Details"
            icon={<UsersIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
            defaultOpen={false}
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-3">
              <div>
                <p className="text-xs text-gray-500">Recruiter Seats</p>
                <p className="text-sm font-medium text-gray-900">{summary.recruiter_seat_count}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Started At</p>
                <p className="text-sm font-medium text-gray-900">{formatDateTime(summary.started_at)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Ended At</p>
                <p className="text-sm font-medium text-gray-900">{formatDateTime(summary.ended_at)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Duration</p>
                <p className="text-sm font-medium text-gray-900">{durationLabel(summary.started_at, summary.ended_at)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Candidate IP Address</p>
                <p className="text-sm font-medium text-gray-900">{summary.candidate_ip_address || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Candidate Location</p>
                <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  {summary.candidate_location && <MapPinIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                  {summary.candidate_location || '—'}
                </p>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Participants ({summary.participants.length})
              </p>
              {summary.participants.length === 0 ? (
                <p className="text-sm text-gray-500">No participants have joined this session yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                        <th className="py-2 pr-4 font-medium">Name</th>
                        <th className="py-2 pr-4 font-medium">Joined At</th>
                        <th className="py-2 pr-4 font-medium">Left At</th>
                        <th className="py-2 pr-4 font-medium">Connection State</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {summary.participants.map((participant: LiveInterviewSummaryParticipant, i: number) => (
                        <tr key={i}>
                          <td className="py-2 pr-4 text-gray-900">
                            {participant.name || roleLabel(participant.role)}
                            <span className="ml-1.5 text-xs text-gray-400">({roleLabel(participant.role)})</span>
                          </td>
                          <td className="py-2 pr-4 text-gray-700">{formatDateTime(participant.joined_at)}</td>
                          <td className="py-2 pr-4 text-gray-700">{formatDateTime(participant.left_at)}</td>
                          <td className="py-2 pr-4">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${CONNECTION_STATE_BADGE_CLASSES[participant.connection_state] || CONNECTION_STATE_BADGE_CLASSES.not_connected}`}>
                              {CONNECTION_STATE_LABELS[participant.connection_state] || participant.connection_state}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Section>

          {/* ---- Integrity Events - same card as Proctored's scorecard
                (IntegritySummaryCard there): total in the header, a badge
                per event type with its count (always visible), and on expand
                the AI paragraph plus the full timed list. Covers the pre-join
                checks, camera/mic detections during the call (both modes)
                and, in human mode, tab switches / blocked copy-paste / an
                extra display - see utils/liveInterviewIntegrity.
                Informational entries (e.g. "Camera & microphone allowed")
                are listed but not counted. --------------------------------- */}
          {(() => {
            const events = summary.integrity_events || []
            const breakdown = summary.integrity_breakdown || {}
            const total = summary.integrity_total ?? events.filter((e: LiveInterviewSummaryIntegrityEvent) => isIntegrityFlag(e.event_type)).length
            const badges = Object.entries(breakdown).map(([type, count]) => {
              const Icon = LIVE_INTEGRITY_EVENT_ICONS[type] || ExclamationTriangleIcon
              return (
                <span
                  key={type}
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap bg-amber-50 text-amber-700"
                >
                  <Icon className="w-2.5 h-2.5 flex-shrink-0" />
                  {integrityEventLabel(type)}: {count}
                </span>
              )
            })
            return (
              <Section
                title="Integrity Events"
                icon={<ShieldCheckIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                summary={
                  <span className={`text-sm font-bold leading-none whitespace-nowrap ${total > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {total} total event{total === 1 ? '' : 's'}
                  </span>
                }
                peek={badges.length > 0 && <div className="flex flex-wrap justify-end gap-1.5">{badges}</div>}
                defaultOpen={false}
              >
                {summary.integrity_summary && (
                  <p className="pt-3 text-xs text-gray-600 leading-relaxed">{summary.integrity_summary}</p>
                )}
                {events.length > 0 && (
                  <div className="pt-3 max-h-64 overflow-y-auto flex flex-col gap-1.5">
                    {events.map((event: LiveInterviewSummaryIntegrityEvent, i: number) => {
                      const flag = isIntegrityFlag(event.event_type)
                      const Icon = flag ? (LIVE_INTEGRITY_EVENT_ICONS[event.event_type] || ExclamationTriangleIcon) : CheckCircleIcon
                      return (
                        <div key={i} className="flex items-center gap-2.5 text-sm">
                          <Icon className={`w-4 h-4 flex-shrink-0 ${flag ? 'text-amber-500' : 'text-gray-400'}`} />
                          <span className={flag ? 'text-gray-700' : 'text-gray-500'}>{integrityEventLabel(event.event_type)}</span>
                          <span className="text-xs text-gray-400 ml-auto">{formatDateTime(event.occurred_at)}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </Section>
            )
          })()}

          {/* ---- Recording. --------------------------------------------- */}
          <Section
            title="Recording"
            icon={<VideoCameraIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
            defaultOpen={false}
          >
            {!summary.recording?.has_recording ? (
              <p className="text-sm text-gray-500 pt-3">No recording is available for this session.</p>
            ) : (
              <div className="pt-3">
                {summary.recording.duration_seconds != null && (
                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                    <ClockIcon className="w-3.5 h-3.5" /> Duration: {formatDuration(summary.recording.duration_seconds)}
                  </p>
                )}
                {summary.recording.playback_url ? (
                  <video
                    ref={recordingVideoRef}
                    className="w-full rounded-lg max-h-[480px] bg-black"
                    controls
                    src={summary.recording.playback_url}
                  />
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <VideoCameraSlashIcon className="w-4 h-4" />
                    The recording will be available for playback once the session is completed.
                  </div>
                )}
              </div>
            )}
          </Section>

          <Section
            title="Candidate Feedback"
            icon={
              <ChatBubbleLeftRightIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
            }
            defaultOpen={false}
          >   
          {/* ---- Candidate Feedback ---- */}
          {summary.candidate_feedback && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex-1">
                  Candidate Feedback
                </p>
                {summary.candidate_feedback.submitted_at && (
                  <span className="text-xs text-gray-400">
                    {new Date(summary.candidate_feedback.submitted_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
              {/* Star rating */}
              {summary.candidate_feedback.rating != null && (
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className={`w-4 h-4 ${star <= summary.candidate_feedback!.rating ? 'text-amber-400' : 'text-gray-200'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                  <span className="text-xs font-semibold text-gray-700 ml-1">{summary.candidate_feedback.rating}/5</span>
                </div>
              )}
              {/* Tags */}
              {summary.candidate_feedback.tags && summary.candidate_feedback.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {summary.candidate_feedback.tags.map((tag) => {
                    const isNegative = tag === 'technical_issues' || tag === 'setup_difficult'
                    const label = {
                      instructions_clear: 'Instructions were clear',
                      platform_easy: 'Platform was easy to use',
                      questions_relevant: 'Questions were relevant',
                      enough_time: 'Had enough time',
                      technical_issues: 'Experienced technical issues',
                      setup_difficult: 'Camera / mic setup was difficult',
                    }[tag] || tag.replace(/_/g, ' ')
                    return (
                      <span key={tag} className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${isNegative ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' : 'bg-gray-100 text-gray-600'}`}>
                        {label}
                      </span>
                    )
                  })}
                </div>
              )}
              {/* Comment */}
              {summary.candidate_feedback.comment && (
                <p className="text-xs text-gray-600 italic">"{summary.candidate_feedback.comment}"</p>
              )}
            </div>
          )}
          </Section>
        </>
      )}
    </div>
    <ReviewReasonModal
      action={reviewAction}
      reason={reviewReason}
      onReasonChange={setReviewReason}
      submitting={reviewMutation.isPending}
      onCancel={() => { setReviewAction(null); setReviewReason('') }}
      onConfirm={() => {
        if (reviewAction) reviewMutation.mutate({ status: reviewAction, reason: reviewReason })
      }}
    />
    </>
  )
}
